import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Badge, Card, CardBody, CardText, CardTitle, Col, Row } from 'reactstrap';
import { CashierBadgeData, CashierError, CashierInfo } from '../../../shared/user/cashier';
import {
  ChildRegistrations,
  DateT,
  ElementHeader,
  HotkeyButton,
  LinkButton,
  Loading,
  MaterialIcon,
  UserStateComponent,
} from '../../components';
import { BadgePreviewRenderer, transformNodeMap } from '../../modules/badgerenderer/BadgeRender';
import { useTranslation } from '../../translations';
import { checkPermission, useConvention, useFetcher, useUserSafe } from '../../utils';
import { IDValidation } from './IDValidationModal';
import { PrintLogModal } from './PrintLogs';
import { AttendeeInformation, AttendeeInformationModalState } from './attendeeInformation';
import { AttendeeStatus, UndeliveredProductsList } from './attendeeStatus';
import { CashierContext } from './cashiercontext';

import './profile.scss';

// eslint-disable-next-line max-lines-per-function
export const CashierProfile: FC = () => {
  const { ts } = useTranslation();
  const match = useRouteMatch<{ id: string }>();
  const cashierSettings = useContext(CashierContext);
  const location = useLocation();
  const history = useHistory();
  const convention = useConvention();
  const id = Number.parseInt(match.params.id, 10);
  const params = new URLSearchParams(location.search);
  const [barcode, setBarcode] = useState<string | null>(params.get('barcode'));
  const [editModalState, setEditModalState] = useState<AttendeeInformationModalState>(null);
  const [printLogsModalState, setPrintLogsModalState] = useState(false);
  const cashier = useUserSafe();

  useEffect(() => {
    // If a modal is open, disable the ESC hotkey
    cashierSettings.updateCashierSettings({
      enableESCHotkey: editModalState === null,
    });
    // Causes an update infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editModalState]);

  const fetch = useFetcher(async () => {
    const profile = await api.getUserCashierProfile(id, barcode ?? undefined);
    const children = profile.registration
      ? await api.getChildRegistration(profile.registration.id)
      : [];

    const hasBarcodeNotBeenScanned = profile.info.some(
      (i) => i.code === CashierInfo.NoBarcodeScanned,
    );

    if (cashierSettings.config.requireIDScan && hasBarcodeNotBeenScanned) {
      profile.info = profile.info.filter((i) => i.code !== CashierInfo.NoBarcodeScanned);
      profile.errors.push({ code: CashierError.NoBarcodeScanned });
      profile.status = 'error';
    }

    return { profile, children };
  }, [id, barcode, cashierSettings.config.requireIDScan]);

  const openUpdateFromScannerModal = useCallback(() => {
    const scannedInformation = fetch.data?.profile.scannedInformation;
    if (!scannedInformation) {
      return;
    }

    setEditModalState({ type: 'user', scannedInformation });
  }, [fetch.data?.profile.scannedInformation]);

  const onIdVerifierOpen = useCallback(() => {
    cashierSettings.updateCashierSettings({
      enableESCHotkey: false,
    });
  }, [cashierSettings]);

  const onIdVerifierClose = useCallback(
    (newBarcode?: string) => {
      cashierSettings.updateCashierSettings({
        enableESCHotkey: true,
      });

      if (newBarcode) {
        setBarcode(newBarcode);
        fetch.refresh();
      }
    },
    [fetch, cashierSettings],
  );

  if (fetch.loading) {
    return (
      <UserStateComponent>
        <Loading inline />
      </UserStateComponent>
    );
  }

  if (fetch.error) {
    return <ProfileError />;
  }

  const { refresh, data } = fetch;
  const { children, profile } = data;
  const { bannedWords, user, isMinor, registration, badge } = profile;

  return (
    <UserStateComponent>
      <ElementHeader
        actions={[
          {
            id: 'view-profile',
            text: ts('view_user_profile'),
            href: `/housekeeping/attendees/user/${user.id}`,
          },
          { id: 'divider-1', divider: true },
          {
            id: 'manage-registration',
            text: `${ts(registration ? 'common.update' : 'common.create')} ${ts(
              'registration',
            ).toLowerCase()}`,
            onClick: () => {
              setEditModalState({ type: 'reg' });
            },
          },
          { id: 'divider-2', divider: true },
          {
            id: 'update-personal-info',
            text: ts('update_personal_details'),
            onClick: () => {
              setEditModalState({ type: 'user' });
            },
            disabled: !checkPermission(cashier, 'user:update'),
          },
          { id: 'divider-3', divider: true },
          {
            id: 'view-print-logs',
            text: ts('cashier_profile_actions_print_logs'),
            onClick: () => {
              setPrintLogsModalState(true);
            },
          },
        ]}
        icon={{ url: user.profilePictureUrl }}
        rightContent={
          <div className="cashier-profile-back">
            <HotkeyButton
              actionText="common.go_back"
              color="secondary"
              disabled={!cashierSettings.config.enableESCHotkey}
              hotkey="Escape"
              onClick={() => {
                history.push('/housekeeping/cashier');
              }}
              outline
            >
              {ts('back_to_user_search')}
            </HotkeyButton>
          </div>
        }
        title={
          <div className="cashier-profile-header-userinfo">
            <div className="cashier-profile-header-userinfo-section">
              <div>#{user.id}</div>
              <div>{ts('id')}:</div>
            </div>
            {user.preferredName && (
              <div className="cashier-profile-header-userinfo-section">
                <div>({user.preferredName})</div>
                <div>{ts('preferred')}</div>
              </div>
            )}
            <div className="cashier-profile-header-userinfo-section">
              <div>{user.firstName}</div>
              <div>{ts('common.first_name')}</div>
            </div>
            <div className="cashier-profile-header-userinfo-section">
              <div>{user.lastName}</div>
              <div>{ts('common.last_name')}</div>
            </div>
            <div className="cashier-profile-header-userinfo-section">
              <div>
                {user.bornAt ? (
                  <DateT options={{ timeZone: 'UTC' }} value={user.bornAt} />
                ) : (
                  ts('common.unknown')
                )}
              </div>
              <div>{ts('common.date_of_birth')}</div>
            </div>
            <div className="cashier-profile-header-userinfo-section">
              <div>{profile.registration?.paidOrderItem?.name || <i>{ts('no_order_yet')}</i>}</div>
              <div>{ts('registration_level')}</div>
            </div>
          </div>
        }
      >
        <span className="badge-list compact">
          {profile.info.some((s) => s.code === CashierInfo.ReturningAttendee) && (
            <Badge className="returning-attendee" color="info">
              {ts('common.returning_attendee')}
            </Badge>
          )}
        </span>
      </ElementHeader>
      <IDValidation id={id} onClose={onIdVerifierClose} onOpen={onIdVerifierOpen} />
      <AttendeeInformation
        bannedWords={bannedWords}
        isMinor={isMinor}
        modalState={editModalState}
        onModalClose={() => {
          setEditModalState(null);
        }}
        registration={registration}
        reload={refresh}
        user={user}
      />
      {printLogsModalState && (
        <PrintLogModal
          close={() => {
            setPrintLogsModalState(false);
          }}
          userId={profile.user.id}
        />
      )}
      <Row className="justify-content-center" id="dashboard">
        <Col className="mb-10" md={6} xs={12}>
          <AttendeeStatus
            openUpdateFromScannerModal={openUpdateFromScannerModal}
            profile={profile}
            reload={refresh}
          />
          {!barcode && (
            <div className="mt-10">
              <div className="cashier-badge-helptext">
                <MaterialIcon name="info" small />
                <div>{ts('cashier_ui.scan_to_verify_id')}</div>
              </div>
            </div>
          )}
          <UndeliveredProductsList
            products={profile.undeliveredProducts}
            refresh={refresh}
            userId={profile.user.id}
          />
        </Col>
        <Col className="cashier-badge-preview mb-10" md={6} xs={12}>
          <h4>{ts('badge_print_preview')}</h4>
          <Card>
            <CardBody>
              <BadgePreview badge={badge} badgeArtId={registration?.badgeArtId ?? undefined} />
            </CardBody>
          </Card>
          <div className="mt-10">
            <div className="cashier-badge-helptext">
              <MaterialIcon name="info" small />
              <div>{ts('actual_badge_print_and_label')}</div>
            </div>
            <div className="cashier-badge-helptext">
              <MaterialIcon name="info" small /> {ts('cashier.labels_black_text')}&nbsp;
              {ts('emojis_will_be_black_and')}
            </div>
          </div>

          {registration && convention.maxChildren > 0 && (
            <div className="mt-10">
              <CardTitle>{ts('child_badges')}</CardTitle>
              <ChildRegistrations
                compact
                hideWarning
                items={children}
                onUpdate={refresh}
                registrationId={registration.id}
                useModal
              />
            </div>
          )}
        </Col>
        <Col md={6} xs={12} />
      </Row>
    </UserStateComponent>
  );
};

const ProfileError: FC = () => {
  const { ts } = useTranslation();
  return (
    <UserStateComponent>
      <Row className="justify-content-center" id="profileNotFound">
        <Col lg={6} xs={12}>
          <Card className="danger">
            <CardBody className="text-center">
              <MaterialIcon large name="error" type="danger" />
              <CardText>{ts('the_specified_user_does_not')}</CardText>
            </CardBody>
          </Card>
        </Col>
        <Col className="mb-10" xs={12} />
        <Col lg={6} xs={12}>
          <LinkButton block color="primary" to="/housekeeping/cashier">
            {ts('back_to_search')}
          </LinkButton>
        </Col>
      </Row>
    </UserStateComponent>
  );
};

interface BadgePreviewProps {
  readonly badgeArtId?: number;
  readonly badge?: CashierBadgeData;
}

const BadgePreview: FC<BadgePreviewProps> = ({ badge, badgeArtId }) => {
  const { ts } = useTranslation();
  const { config } = useContext(CashierContext);
  if (!badge?.labels[0]) {
    return (
      <div className="cashier-badge-empty-state" id="noBadgePreview">
        <h4>{ts('no_badge_preview_available')}</h4>
        <p>{ts('ensure_the_user_is_registered')}</p>
      </div>
    );
  }

  return badge.labels.map((label, id) => {
    return (
      <BadgePreviewRenderer
        image={badgeArtId ? `/api/badgeart/${badgeArtId}/image` : ''}
        // eslint-disable-next-line react/no-array-index-key
        key={id}
        label={label}
        nodes={transformNodeMap(config.overrideDesign ?? badge.design)}
      />
    );
  });
};
